import { groqChatCompletion } from './groqClient';

/**
 * Reviews all player answers for a multiplayer round using AI.
 * Scores each category in a separate API call so results arrive quickly
 * and progress can be shown. Max score per category is 10.
 *
 * @param {string}   letter      - Round letter
 * @param {string[]} categories  - Category list
 * @param {Array}    submissions - Array of { userId, playerName, answers: {[cat]: string} }
 * @param {Function} onCategoryDone - optional callback(category, scores) called after each category
 * @returns {Promise<Object>} - { [userId]: { [category]: number, total: number } }
 */
export async function reviewMultiplayerAnswers(letter, categories, submissions, onCategoryDone) {
  if (!submissions || submissions.length === 0) return {};

  // Use short keys p0..pN so the model doesn't mangle Firebase IDs
  const keyMap = {}; // p0 -> userId
  submissions.forEach((s, i) => { keyMap[`p${i}`] = s.userId; });

  // Initialise result object
  const result = {};
  submissions.forEach((s) => {
    result[s.userId] = { total: 0, specialBonus: 0 };
    categories.forEach((c) => { result[s.userId][c] = 0; });
  });

  // Score each category independently — faster responses, shows progress
  for (const category of categories) {
    const answerLines = submissions.map((s, i) => {
      const answer = s.answers?.[category]?.trim() || '';
      return `  p${i} (${s.playerName}): "${answer}"`;
    }).join('\n');

    const exampleResult = submissions.map((_, i) => `"p${i}": 0`).join(', ');

    const prompt = `Judge these answers for the category "${category}" in a word game. Letter: "${letter}".

Rules:
1. Must start with "${letter}" — if not, 0.
2. Must be a real ${category} — if not, 0.
3. Blank = 0.
4. Minor typo (word recognisable) still counts as valid. Heavy misspelling: 0.
5. Accept names/places from ANY culture (Nigerian, African, Arabic, Asian, Latin etc.).

Score each answer 10 if valid, 0 if invalid. Don't lower scores for common or shared answers; the game handles that.

Answers:
${answerLines}

Return ONLY JSON: { ${exampleResult} }`;

    try {
      const raw = await groqChatCompletion({
        messages: [{ role: 'user', content: prompt }],
        model: 'qwen/qwen3.8-27b',
        reasoning_effort: 'none',
        temperature: 0.1,
        max_tokens: 150,
        response_format: { type: 'json_object' },
      });

      let parsed;
      try {
        parsed = JSON.parse(raw);
      } catch {
        const match = raw?.match(/\{[\s\S]*\}/);
        parsed = match ? JSON.parse(match[0]) : {};
      }

      // Map short keys back to userIds
      const valid = submissions.map((_, i) => (parseInt(parsed[`p${i}`], 10) || 0) > 0);
      applyPoints(result, category, submissions, valid);
      if (onCategoryDone) onCategoryDone(category);
    } catch (err) {
      console.error(`[MultiplayerAIReview] Failed for category "${category}", using fallback:`, err);
      // Fallback for this category only
      const valid = submissions.map((s) => fallbackValid(letter, s.answers?.[category]));
      applyPoints(result, category, submissions, valid);
      if (onCategoryDone) onCategoryDone(category);
    }
  }

  return result;
}

function fallbackValid(letter, answer) {
  const w = answer?.trim() || '';
  return w.length >= 2 && w[0]?.toUpperCase() === letter.toUpperCase();
}

// The results legend: 10 = valid and unique, 5 = valid but shared, 0 = invalid.
function applyPoints(result, category, submissions, valid) {
  const counts = {};
  submissions.forEach((s, i) => {
    const key = s.answers?.[category]?.trim().toLowerCase();
    if (valid[i] && key) counts[key] = (counts[key] || 0) + 1;
  });
  submissions.forEach((s, i) => {
    const key = s.answers?.[category]?.trim().toLowerCase();
    const score = !valid[i] || !key ? 0 : counts[key] > 1 ? 5 : 10;
    result[s.userId][category] = score;
    result[s.userId].total += score;
  });
}
